import { cn } from '@module/lib/utils';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

/** variant → 클래스 맵 (tailwind.css에 대응 유틸이 정의돼 있어야 함) */
export const VARIANTS = {
  'display-1': 'font-display-1',
  'display-2': 'font-display-2',
  'display-3': 'font-display-3',
  'display-4': 'font-display-4',

  'heading-1': 'font-heading-1',
  'heading-2': 'font-heading-2',
  'heading-3': 'font-heading-3',
  'heading-4': 'font-heading-4',

  'title-1': 'font-title-1',
  'title-2': 'font-title-2',
  'title-3': 'font-title-3',
  'title-4': 'font-title-4',

  'body-1': 'font-body-1',
  'body-2': 'font-body-2',
  'body-3': 'font-body-3',
  'body-4': 'font-body-4',

  'caption-1': 'font-caption-1',
  'caption-2': 'font-caption-2',
  'caption-3': 'font-caption-3',
  'caption-4': 'font-caption-4',
  'caption-5': 'font-caption-5',
} as const;

export const COLORS = {
  primary: 'text-brand-primary',
  secondary: 'text-brand-secondary',
  tertiary: 'text-brand-tertiary',
  black: 'text-system-black',
  gray900: 'text-system-gray-900',
  gray700: 'text-system-gray-700',
  gray500: 'text-system-gray-500',
  gray300: 'text-system-gray-300',
  gray100: 'text-system-gray-100',
  grey50: 'text-system-gray-50',
  white: 'text-system-white',
  pressed: 'text-system-pressed',
  tag1: 'text-system-tag1',
  red1: 'text-system-red1',
  red2: 'text-system-red2',
  blue: 'text-system-blue',
  yellow: 'text-system-yellow',
  purple1: 'text-system-purple1',
  purple2: 'text-system-purple2',
  pinkBtn: 'text-system-pink-btn',
  inherit: '',
} as const;

export type Color = keyof typeof COLORS;

export type Variant = keyof typeof VARIANTS;

export const WEIGHTS = {
  regular: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
} as const;

export const textVariants = cva('', {
  variants: {
    variant: VARIANTS,
    weight: WEIGHTS,
    color: COLORS,
  },
  defaultVariants: {
    weight: 'regular',
    color: 'primary',
  },
});

export type TypographyProps = Omit<React.ComponentPropsWithoutRef<'p'>, 'color'> &
  VariantProps<typeof textVariants> & {
    /** true면 Slot으로 자식 요소를 그대로 렌더 (폴리모픽) */
    asChild?: boolean;
  } & {
    // cva의 VariantProps는 optional이므로 variant를 required로 다시 고정
    variant: Variant;
  };

export const Typography = React.forwardRef<HTMLParagraphElement, TypographyProps>(
  ({ asChild, variant, weight = 'regular', color = 'primary', className, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'p';

    const classes = cn('antialiased', textVariants({ variant, weight, color }), className);

    return (
      <Comp ref={asChild ? undefined : ref} className={classes} {...props}>
        {children}
      </Comp>
    );
  },
);

Typography.displayName = 'Typography';
